import {makeStyles, TextField} from '@material-ui/core';
import Axios from 'axios';
import MaterialTable, {MTableToolbar} from 'material-table';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {calculateReadingValues} from '../../utils/calculateReadingValues';
import {tableIcons} from '../../styles/tableIcons';
import {tableLocalization} from '../../styles/tableLocalization';
import TableTitle from '../utils/TableTitle';

const useStyles = makeStyles(({palette}) => ({
  toolbar: {
    paddingRight: '8px',
    textTransform: 'capitalize',
    color: palette.meterTypeTextColor,
    background: palette.meterTypeColor,
    '& svg': {
      color: palette.meterTypeTextColor,
    },
  },
}));

export const DashboardUserMeterReadingsTable = ({meter, user, setMeter}) => {
  const classes = useStyles(meter);

  const tableReadings = meter.readings?.filter((reading) => {
    return reading.deleted !== true;
  });

  const isUtilityPortal = meter.dataSource === 'utility-portal';
  const isCalculatedFromValue =
    meter.dataSource === 'meter' ||
    meter.dataSource === 'audit' ||
    meter.dataSource === '' ||
    !meter.dataSource;

  const dateColumn = {
    title: 'Date',
    field: 'time',
    type: 'date',
    render: (rowData) => moment(rowData.time).format('LL'),
    editComponent: (props) => (
      <TextField
        inputProps={{'aria-label': 'Date'}}
        type="date"
        value={props.value ? moment(props.value).format('YYYY-MM-DD') : ''}
        onChange={(e) => {
          if (!e.target.value) return props.onChange(null);
          const picked = moment(e.target.value, 'YYYY-MM-DD');
          // preserve the existing time-of-day when only the date changes
          const merged = (props.value ? moment(props.value) : moment())
            .year(picked.year())
            .month(picked.month())
            .date(picked.date());
          props.onChange(merged.toDate());
        }}
      />
    ),
    defaultSort: 'desc',
  };
  const timeColumn = {
    title: 'Time',
    field: 'time',
    type: 'time',
    render: (rowData) => moment(rowData.time).format('hh:mm A'),
  };
  const valueColumn = {
    title: 'Value',
    field: 'value',
    type: 'numeric',
    editable: isCalculatedFromValue ? 'always' : 'never',
    render: (rowData) =>
      rowData
        ? rowData.value
          ? Number.parseFloat(rowData.value).toFixed(2)
          : 'N/A'
        : 0,
  };
  const consumptionColumn = {
    title: 'Consumption', // same as "Raw Usage"
    field: 'rawUsage',
    type: 'numeric',
    editable: !isCalculatedFromValue ? 'always' : 'never',
    render: (rowData) =>
      rowData ? (rowData.rawUsage ? rowData.rawUsage.toFixed(2) : 'N/A') : '',
  };
  const dailyEquivalentColumn = {
    title: 'Daily Equivalent', // same as "Daily Usage"
    field: 'dailyUsage',
    type: 'numeric',
    editable: isUtilityPortal || !isCalculatedFromValue ? 'always' : 'never',
    render: (rowData) =>
      rowData
        ? rowData.dailyUsage
          ? rowData.dailyUsage.toFixed(2)
          : 'N/A'
        : '',
  };

  const columns = isUtilityPortal
    ? [dateColumn, dailyEquivalentColumn]
    : [
        dateColumn,
        timeColumn,
        valueColumn,
        consumptionColumn,
        dailyEquivalentColumn,
      ];

  const coerceUtilityPortalTime = (reading) => {
    if (!isUtilityPortal) return reading;
    const date = reading.time ? moment(reading.time) : moment();
    reading.time = date.startOf('day').hour(12).toDate();
    return reading;
  };

  const isMeterOwner = meter.user === user._id;

  const handleTableChange = {
    onRowAdd: isMeterOwner
      ? async (newData) => {
          let meterCopy = {...meter}; // copy meter
          let newReading = coerceUtilityPortalTime({...newData});
          meterCopy.readings.push(newReading);
          meterCopy = calculateReadingValues(meterCopy);
          try {
            const dbResponse = await Axios.post(
              `/api/meter/${meterCopy._id}`,
              meterCopy,
            );
            setMeter(calculateReadingValues(dbResponse.data));
          } catch (err) {
            console.error('Error creating new readings', err);
          }
        }
      : null,
    onRowUpdate: isMeterOwner
      ? async (newData, oldData) => {
          let meterCopy = {...meter}; // copy meter
          let index = meterCopy.readings.indexOf(oldData);
          let updatedReading = coerceUtilityPortalTime({...newData});
          meterCopy.readings[index] = updatedReading;
          meterCopy = calculateReadingValues(meterCopy);
          try {
            const dbResponse = await Axios.post(
              `/api/meter/${meterCopy._id}`,
              meterCopy,
            );
            setMeter(calculateReadingValues(dbResponse.data));
          } catch (err) {
            console.error('Error updating reading', err);
          }
        }
      : null,
    onRowDelete: isMeterOwner
      ? async (oldData) => {
          let meterCopy = {...meter}; // copy meter
          let index = meterCopy.readings.indexOf(oldData);
          const softDeletedReading = {
            ...oldData,
            deleted: true,
          };
          meterCopy.readings[index] = softDeletedReading;
          meterCopy = calculateReadingValues(meterCopy);
          try {
            const dbResponse = await Axios.post(
              `/api/meter/${meter._id}`,
              meterCopy,
            );
            setMeter(calculateReadingValues(dbResponse.data));
          } catch (error) {
            console.error(error);
          }
        }
      : null,
  };

  return (
    <MaterialTable
      localization={tableLocalization}
      title={<TableTitle>{`${meter.type} Data Table`}</TableTitle>}
      data={tableReadings}
      components={{
        Toolbar: (props) => (
          <div className={classes.toolbar}>
            <MTableToolbar {...props} />
          </div>
        ),
      }}
      options={{
        // Column drag handles render a role="button" inside each sortable
        // header button (nested interactive controls, WCAG 4.1.2).
        draggable: false,
        search: false,
        paging: false,
        minBodyHeight: '400px',
        pageSize: 5,
        actionsColumnIndex: -1,
        addRowPosition: 'first',
        actionsCellStyle: {
          paddingLeft: '20px',
        },
      }}
      columns={columns}
      editable={handleTableChange}
      icons={tableIcons}
    />
  );
};

DashboardUserMeterReadingsTable.prototype = {
  user: PropTypes.object.isRequired,
  meter: PropTypes.object.isRequired,
  setMeter: PropTypes.func,
};
